import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Flex } from "@radix-ui/themes";
import Lowlight from "react-lowlight";
import styled from "@emotion/styled";
import { atlasRoutes } from "@/app/router/routes";

export const ApiRequestRecordsViewPage: FC = () => {
  const navigate = useNavigate();
  const { apiRequestRecordId } = useParams<{ apiRequestRecordId: Uuid }>();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!apiRequestRecordId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["api-request-record-by-id", apiRequestRecordId],
    queryFn: () =>
      api.get("/internal/v1/api-request-records/{api_request_record_id}", {
        params: {
          path: {
            api_request_record_id: apiRequestRecordId,
          },
        },
      }),
    staleTime: 60 * 1000,
  });

  const handleReprocess = async () => {
    const ok = window.confirm("Reprocess this API request record? This may create or modify data.");
    if (!ok) return;
    setIsProcessing(true);
    try {
      await api.post("/internal/v1/api-request-records/{api_request_record_id}/process", {
        params: {
          path: { api_request_record_id: apiRequestRecordId },
        },
      });
      alert("Reprocessing started/succeeded.");
    } catch (err) {
      alert(`Failed to reprocess: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewAssociatedSession = () => {
    const associatedSessionId = result.data?.data?.associated_session_id;
    if (!associatedSessionId) {
      alert("No associated session.");
      return;
    }

    navigate(atlasRoutes.sessionEvents({ params: { sessionId: associatedSessionId } }).path);
  };

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError || !result.data) {
    return <div>Error: {result.error?.message ?? "Unknown error"}</div>;
  }

  return (
    <Container>
      <br />
      <ButtonGroup>
        <Button variant="outline" disabled={isProcessing} onClick={handleReprocess}>
          {isProcessing ? "Processing..." : "Reprocess"}
        </Button>
        <Button variant="soft" onClick={handleViewAssociatedSession}>
          View Associated Session
        </Button>
      </ButtonGroup>

      <br />
      <h1>View API Request Record</h1>
      <Lowlight language="json" value={JSON.stringify(result.data, null, 2)} markers={[]} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const ButtonGroup = styled(Flex)`
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;
